import React from 'react';

interface Props {
  label?: string;
  error?: string;
  hint?: string;
  prefix?: string;
  type?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  step?: string;
  min?: string;
  max?: string;
  pattern?: string;
  className?: string;
  autoComplete?: string;
}

export function Input({
  label,
  error,
  hint,
  prefix,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  name,
  id,
  autoFocus,
  required,
  disabled,
  inputMode,
  step,
  min,
  max,
  pattern,
  className = '',
  autoComplete,
}: Props) {
  const inputId = id ?? name;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        {prefix && (
          <span className="absolute left-3 text-gray-500 text-base font-medium select-none">
            {prefix}
          </span>
        )}
        <input
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          inputMode={inputMode}
          step={step}
          min={min}
          max={max}
          pattern={pattern}
          autoComplete={autoComplete}
          className={[
            'w-full h-12 rounded-xl border text-base',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500',
            prefix ? 'pl-8 pr-4' : 'px-4',
            error
              ? 'border-red-400 bg-red-50 focus:ring-red-400'
              : 'border-gray-300 bg-white focus:border-emerald-500',
            disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : '',
          ].join(' ')}
        />
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

interface TextareaProps {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  name?: string;
  id?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
}

export function Textarea({
  label,
  error,
  hint,
  placeholder,
  value,
  onChange,
  name,
  id,
  rows = 3,
  disabled,
  className = '',
}: TextareaProps) {
  const inputId = id ?? name;
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={rows}
        disabled={disabled}
        className={[
          'w-full rounded-xl border px-4 py-3 text-base resize-none',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500',
          error
            ? 'border-red-400 bg-red-50 focus:ring-red-400'
            : 'border-gray-300 bg-white focus:border-emerald-500',
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : '',
        ].join(' ')}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
